import { createContext, useContext, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, Calculator, MessageCircle } from "lucide-react";
import { submitLead } from "@/lib/api";
import { BRAND, PROJECT_TYPES, BUDGETS, ESTIMATOR } from "@/lib/data";

const EnquiryContext = createContext(null);
export const useEnquiry = () => useContext(EnquiryContext);

const waLink = (text) => `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(text)}`;
const inr = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

function Choice({ items, value, onSelect, label }) {
  return (
    <div className="space-y-2">
      <Label className="uppercase text-xs tracking-[0.15em] text-muted-foreground">{label}</Label>
      <div className="flex flex-wrap gap-2">
        {items.map((it) => (
          <button key={it.key} type="button" onClick={() => onSelect(it)} data-testid={`est-${label}-${it.key}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${value.key === it.key ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}>
            {it.key}
          </button>
        ))}
      </div>
    </div>
  );
}

function LeadForm({ prefill, onDone }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", project_type: prefill?.project_type || "", budget: "", message: prefill?.message || "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Please enter your name and phone."); return; }
    setLoading(true);
    try {
      await submitLead({ ...form, source: prefill?.source || "enquiry" });
      toast.success("Thank you! Our design team will call you shortly.");
      onDone?.();
    } catch { toast.error("Something went wrong. Please try WhatsApp."); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="lead-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Name*</Label>
          <Input data-testid="lead-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" />
        </div>
        <div className="space-y-1.5">
          <Label>Phone*</Label>
          <Input data-testid="lead-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Mobile number" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Email</Label>
        <Input data-testid="lead-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Project Type</Label>
          <Select value={form.project_type} onValueChange={(v) => set("project_type", v)}>
            <SelectTrigger data-testid="lead-project-type"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{PROJECT_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Budget</Label>
          <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
            <SelectTrigger data-testid="lead-budget"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Message</Label>
        <Textarea data-testid="lead-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us about your project" rows={3} />
      </div>
      <div className="flex flex-col sm:flex-row gap-3 pt-1">
        <Button type="submit" disabled={loading} data-testid="lead-submit" className="flex-1 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
          <Send className="w-4 h-4 mr-2" /> {loading ? "Sending..." : "Book Free Consultation"}
        </Button>
        <Button type="button" variant="outline" className="rounded-full border-primary/30" data-testid="lead-whatsapp"
          onClick={() => window.open(waLink(`Hi ${BRAND.name}, I'd like a free consultation. Name: ${form.name || ""}`), "_blank")}>
          <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
        </Button>
      </div>
    </form>
  );
}

function Estimator({ onEnquire }) {
  const [size, setSize] = useState(ESTIMATOR.sizes[1]);
  const [finish, setFinish] = useState(ESTIMATOR.finishes[1]);
  const [layout, setLayout] = useState(ESTIMATOR.layouts[0]);
  const [addons, setAddons] = useState([]);

  const toggle = (a) => setAddons((p) => p.find((x) => x.key === a.key) ? p.filter((x) => x.key !== a.key) : [...p, a]);
  const base = size.area * finish.rate * layout.factor;
  const addTotal = addons.reduce((s, a) => s + a.cost, 0);
  const total = base + addTotal;

  return (
    <div className="space-y-5" data-testid="estimator">
      <Choice items={ESTIMATOR.sizes} value={size} onSelect={setSize} label="size" />
      <Choice items={ESTIMATOR.layouts} value={layout} onSelect={setLayout} label="layout" />
      <Choice items={ESTIMATOR.finishes} value={finish} onSelect={setFinish} label="finish" />
      <div className="space-y-2">
        <Label className="uppercase text-xs tracking-[0.15em] text-muted-foreground">Add-ons</Label>
        <div className="flex flex-wrap gap-2">
          {ESTIMATOR.addons.map((a) => {
            const on = !!addons.find((x) => x.key === a.key);
            return (
              <button key={a.key} type="button" onClick={() => toggle(a)} data-testid={`est-addon-${a.key}`}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "bg-gold text-black border-gold" : "border-border hover:border-gold"}`}>
                {a.key}
              </button>
            );
          })}
        </div>
      </div>
      <div className="rounded-2xl p-6 bg-charcoal text-white grain relative overflow-hidden">
        <p className="text-xs uppercase tracking-[0.2em] text-white/60">Estimated Cost</p>
        <p className="font-display text-4xl mt-1" data-testid="estimator-total">{inr(total * 0.9)} – {inr(total * 1.1)}</p>
        <p className="text-xs text-white/50 mt-2">*Indicative estimate. Final quote after site visit.</p>
      </div>
      <Button onClick={() => onEnquire({ project_type: "Modular Kitchen", message: `Kitchen estimate: ${inr(total)} (${size.key}, ${layout.key}, ${finish.key})`, source: "estimator" })}
        className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="estimator-book">
        Get Exact Quote & Book Consultation
      </Button>
    </div>
  );
}

export function EnquiryProvider({ children }) {
  const [lead, setLead] = useState(null);
  const [estOpen, setEstOpen] = useState(false);

  const open = (prefill = {}) => setLead(prefill);
  const openEstimator = () => setEstOpen(true);

  return (
    <EnquiryContext.Provider value={{ open, openEstimator, waLink }}>
      {children}

      <Dialog open={!!lead} onOpenChange={(o) => !o && setLead(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Book Your Free Consultation</DialogTitle>
            <DialogDescription>Share your details — our design experts will reach out within 24 hours.</DialogDescription>
          </DialogHeader>
          <LeadForm prefill={lead} onDone={() => setLead(null)} />
        </DialogContent>
      </Dialog>

      <Dialog open={estOpen} onOpenChange={setEstOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><Calculator className="w-5 h-5 text-primary" /> Kitchen Cost Estimator</DialogTitle>
            <DialogDescription>Get an instant indicative estimate for your modular kitchen.</DialogDescription>
          </DialogHeader>
          <Estimator onEnquire={(p) => { setEstOpen(false); setTimeout(() => setLead(p), 200); }} />
        </DialogContent>
      </Dialog>
    </EnquiryContext.Provider>
  );
}
